import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { mapRoomsAdditionalParams } from 'src/app/helpers/utils/map.utils';
import { filterRoomsByFeatures } from 'src/app/helpers/utils/room-filter.utils';

import { RoomFilterCriteria } from 'src/app/modules/booking/components/filter-rooms/filter-rooms.models';
import { Room } from 'src/app/shared/models/room.model';

const DISPLAY_PAGE_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class RoomStoreService {
  private readonly rawBuffer$ = new BehaviorSubject<Room[]>([]);
  private readonly displayOffset$ = new BehaviorSubject<number>(DISPLAY_PAGE_SIZE);
  private readonly filterCriteria$ = new BehaviorSubject<RoomFilterCriteria>({});

  private lastFoundRoom: Room | null = null;
  private lastDistanceValue = 0;
  private preFilterOffset: number | null = null;

  readonly rooms$ = combineLatest([this.rawBuffer$, this.displayOffset$, this.filterCriteria$]).pipe(
    map(([buffer, offset, criteria]) => {
      const filtered = filterRoomsByFeatures(buffer, criteria);
      return mapRoomsAdditionalParams(filtered.slice(0, offset));
    }),
    shareReplay(1)
  );

  public addRooms(result: Room[]): void {
    console.log(result);
    const current = this.rawBuffer$.value;
    const existing = new Set(current.map((r) => r.centreNumber));

    const newRooms = result
      .filter((room) => !existing.has(room.centreNumber))
      .map((room) => ({ ...room, distanceValue: this.lastDistanceValue++ }));

    if (newRooms.length > 0) {
      this.rawBuffer$.next([...current, ...newRooms]);
      this.lastFoundRoom = newRooms.at(-1);
    }

  }

  public reset(): void {
    this.rawBuffer$.next([]);
    this.displayOffset$.next(DISPLAY_PAGE_SIZE);
    this.filterCriteria$.next({});
    this.lastFoundRoom = null;
    this.lastDistanceValue = 0;
    this.preFilterOffset = null;
  }

  public setFilterCriteria(criteria: RoomFilterCriteria): void {
    const isActive = !!(criteria.roomConfiguration || criteria.roomFeature);

    if (isActive) {
      this.preFilterOffset = this.displayOffset$.value;
      this.filterCriteria$.next(criteria);
    } else {
      this.filterCriteria$.next(criteria);
      this.displayOffset$.next(this.preFilterOffset ?? DISPLAY_PAGE_SIZE);
      this.preFilterOffset = null;
    }
  }

  public getFilterCriteria(): RoomFilterCriteria {
    return this.filterCriteria$.value;
  }

  public showNextPage(): boolean {
    const filtered = filterRoomsByFeatures(this.rawBuffer$.value, this.filterCriteria$.value);
    const currentOffset = this.displayOffset$.value;

    if (filtered.length > currentOffset) {
      this.displayOffset$.next(currentOffset + DISPLAY_PAGE_SIZE);
      return true;
    }
    return false;
  }

  public hasMoreInBuffer(): boolean {
    const filtered = filterRoomsByFeatures(this.rawBuffer$.value, this.filterCriteria$.value);
    return filtered.length > this.displayOffset$.value;
  }

  public getLastFoundRoom(): Room {
    return this.lastFoundRoom;
  }

  public getRemainingCount(): number {
    const filtered = filterRoomsByFeatures(this.rawBuffer$.value, this.filterCriteria$.value);
    return Math.max(0, filtered.length - this.displayOffset$.value);
  }
}
